import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DateFormatService } from '@lx/shared/i18n';
import { OrdinalPipe } from '@lx/shared/ui/pipes';
import { getDateFromString, getToday } from '@lx/shared/utils';

@Component({
  standalone: true,
  selector: 'lx-urgency-driver',
  templateUrl: './urgency-driver.component.html',
  styleUrls: ['./urgency-driver.component.scss'],
  imports: [OrdinalPipe, NgIf],
})
export class UrgencyDriverComponent implements OnChanges {
  @Input() expiryDate: string;
  description = '';
  endDateInDays = '';
  endDateMonthName = '';
  limitedTimeOffer = false;

  private weeksLeft = 0;
  private readonly limitedTimeOfferThreshold = 4;

  ngOnChanges(changes: SimpleChanges) {
    const expiryDateStr = changes.expiryDate.currentValue;

    if (!expiryDateStr) {
      return;
    }

    const expiryDate = getDateFromString(expiryDateStr);

    this.weeksLeft = this.transformToWholeNumber(this.differenceInWeeks(getToday(), expiryDate));
    this.limitedTimeOffer = this.weeksLeft > this.limitedTimeOfferThreshold;

    this.description = this.getDescription(expiryDate);

    if (!this.limitedTimeOffer) {
      this.endDateInDays = this.getDateInDays(expiryDate);
      this.endDateMonthName = this.getMonthName(expiryDate);
    }
  }

  private getDescription(endDate: Date): string {
    const startDate = getToday();

    const hoursLeft = this.differenceInHours(startDate, endDate);
    const daysLeft = this.transformToWholeNumber(this.differenceInDays(startDate, endDate));

    if (hoursLeft <= 24) {
      return $localize`Only 24 hours left!`;
    }

    if (daysLeft < 7) {
      return this.getDaysLeftMessage(daysLeft);
    }

    if (!this.limitedTimeOffer) {
      return this.getWeeksLeftMessage(this.weeksLeft);
    }

    if (this.limitedTimeOffer) {
      return $localize`Limited time offer!`;
    }
  }

  private getDaysLeftMessage(daysLeft: number): string {
    if (daysLeft === 1) {
      return $localize`Only 1 day left!`;
    }
    return $localize`Only ${daysLeft} days left!`;
  }

  private getWeeksLeftMessage(weeksLeft: number): string {
    if (weeksLeft === 1) {
      return $localize`Only 1 week left!`;
    }
    return $localize`Only ${weeksLeft} weeks left!`;
  }

  private transformMillisecondsToHours(timeInMilliseconds: number): number {
    return timeInMilliseconds / (1000 * 60 * 60);
  }

  private differenceInMilliseconds(startDate: Date, endDate: Date): number {
    return endDate.getTime() - startDate.getTime();
  }

  private differenceInHours(startDate: Date, endDate: Date): number {
    const differenceInMilliseconds = this.differenceInMilliseconds(startDate, endDate);

    return this.transformMillisecondsToHours(differenceInMilliseconds);
  }

  private differenceInDays(startDate: Date, endDate: Date): number {
    return this.differenceInHours(startDate, endDate) / 24;
  }

  private differenceInWeeks(startDate: Date, endDate: Date): number {
    return this.differenceInDays(startDate, endDate) / 7;
  }

  private transformToWholeNumber(number: number): number {
    return Math.floor(number);
  }

  private getDateInDays(date: Date): string {
    return String(date?.getDate());
  }

  private getMonthName(date: Date): string {
    return DateFormatService.getCalendarLocaleSettings().monthNames[date?.getMonth()];
  }
}
